import { createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '@global/Api';
import {
  GET_SUBSCRIPTION,
  GET_SUBSCRIPTION_RESPONSE,
  GET_SUBSCRIPTION_VARIABLES,
} from '@global/requests/subscriptionDetails';
import { getEntitlements } from '@global/store/slices/entitlements/extraReducers/getEntitlements';
import { hasActiveEntitlementForSku } from '@global/hooks/useEntitlementPolling/utils';

import { getSubscriptionStatuses } from '@src/global/utils/getSubscriptionStatuses';
import { RootState } from '@global/store/store';
import { SiteConfig } from '@src/global/utils/SiteConfig';
import { hasActiveB2CSubscription } from './b2cSubscriptionCheck';

export const getSubscriptionsForCheckout = createAsyncThunk<
  boolean,
  { uId: string },
  { rejectValue: string }
>(
  'cart/getSubscriptionsForCheckout',
  async ({ uId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const { cart } = getState() as RootState;

      const cartMaIds = cart.orderItems
        .map((item) => item.maId)
        .filter((maId): maId is string => Boolean(maId));

      if (cartMaIds.length === 0) return false;

      const statuses = getSubscriptionStatuses('checkout');
      const { data } = await client.query<
        GET_SUBSCRIPTION_RESPONSE,
        GET_SUBSCRIPTION_VARIABLES
      >({
        query: GET_SUBSCRIPTION,
        variables: {
          userUid: uId,
          statuses,
        },
      });

      if (data.wGetSubscriptions.result.status === 'ERROR') {
        return rejectWithValue('SERVER_ERROR');
      }

      const edges = data.wGetSubscriptions.result.data.edges;
      if (edges.length === 0) return false;

      /**
       * B2C sites can have active subscriptions even when entitlement data is missing
       * or the product maId differs from the one in the current cart.
       *
       * Check the customer subscriptions first; if active data is already present,
       * block the duplicate purchase immediately. Fall back to entitlement validation
       * only when there is no matching subscription record.
       */
      const { isB2CSite, siteId } = SiteConfig.getConfig();

      if (isB2CSite) {
        if (hasActiveB2CSubscription(edges, cartMaIds, siteId)) {
          return true;
        }

        const productSku = cartMaIds[0];

        try {
          const entitlements = await dispatch(getEntitlements()).unwrap();

          return hasActiveEntitlementForSku(entitlements, productSku);
        } catch (error) {
          return true;
        }
      }

      const existingDuplicateSubscription = edges.find((edge) => {
        return edge.node.hiConsistsOf.some(({ biiSalable }) =>
          cartMaIds.includes(biiSalable.maId)
        );
      });

      return Boolean(existingDuplicateSubscription);
    } catch (e) {
      return rejectWithValue('SERVER ERROR');
    }
  }
);
